/* IWAO product video reels — snippets/iwao-product-video-reels.liquid */

if (!customElements.get('iwao-video-reels')) {
  class IwaoVideoReels extends HTMLElement {
    constructor() {
      super();
      this.index = 0;
      this.muted = false;
      this.lastFocus = null;
      this.closeTimer = null;
      this.touchStart = null;

      this.onKeydown = this.onKeydown.bind(this);
      this.updateNav = this.updateNav.bind(this);
    }

    connectedCallback() {
      if (!this.modal) {
        this.track = this.querySelector('[data-reels-track]');
        this.prevButton = this.querySelector('[data-reels-prev]');
        this.nextButton = this.querySelector('[data-reels-next]');
        this.tiles = Array.from(this.querySelectorAll('[data-reels-open]'));
        this.previews = Array.from(this.querySelectorAll('[data-reels-preview]'));
        this.modal = this.querySelector('[data-reels-modal]');
        if (!this.track || !this.modal) return;

        this.stage = this.modal.querySelector('[data-reels-stage]');
        this.slides = Array.from(this.modal.querySelectorAll('[data-reels-slide]'));
        this.videos = this.slides.map((slide) => slide.querySelector('[data-reels-video]'));
        this.closeButton = this.modal.querySelector('[data-reels-close]');
        this.modalPrev = this.modal.querySelector('[data-reels-modal-prev]');
        this.modalNext = this.modal.querySelector('[data-reels-modal-next]');
        this.soundButtons = Array.from(this.modal.querySelectorAll('[data-reels-sound]'));

        this.bindSlider();
        this.bindModal();
      }

      // The lightbox lives on <body> so sticky/overflow ancestors can't clip it.
      if (this.modal && this.modal.parentElement !== document.body) {
        document.body.appendChild(this.modal);
      }
      this.observePreviews();
    }

    disconnectedCallback() {
      if (this.previewObserver) this.previewObserver.disconnect();
      if (!this.modal) return;
      if (this.isOpen()) this.close(false);
      // Take the lightbox back so it is removed with this element (theme editor re-renders).
      this.appendChild(this.modal);
    }

    /* Inline slider */

    bindSlider() {
      const step = () => {
        const slide = this.track.firstElementChild;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        return slide ? slide.getBoundingClientRect().width + gap : this.track.clientWidth;
      };

      this.prevButton.addEventListener('click', () => this.track.scrollBy({ left: -step(), behavior: 'smooth' }));
      this.nextButton.addEventListener('click', () => this.track.scrollBy({ left: step(), behavior: 'smooth' }));
      this.track.addEventListener('scroll', this.updateNav, { passive: true });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.updateNav);
        this.resizeObserver.observe(this.track);
      }
      this.updateNav();

      this.tiles.forEach((tile) => {
        tile.addEventListener('click', () => this.open(Number(tile.dataset.reelsOpen), tile));
      });
    }

    updateNav() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      this.classList.toggle('is-scrollable', max > 2);
      this.prevButton.disabled = this.track.scrollLeft <= 2;
      this.nextButton.disabled = this.track.scrollLeft >= max - 2;
    }

    canAutoplayPreviews() {
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const saveData = navigator.connection && navigator.connection.saveData;
      return !reducedMotion && !saveData;
    }

    observePreviews() {
      if (!this.previews.length || !this.canAutoplayPreviews() || !('IntersectionObserver' in window)) return;
      if (this.previewObserver) this.previewObserver.disconnect();

      this.previewObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            video.dataset.inView = entry.intersectionRatio >= 0.25 ? 'true' : 'false';
            if (entry.isIntersecting) this.load(video);
            if (video.dataset.inView === 'true' && !this.isOpen()) {
              this.play(video);
            } else {
              video.pause();
            }
          });
        },
        { threshold: [0, 0.25] }
      );
      this.previews.forEach((video) => this.previewObserver.observe(video));
    }

    resumePreviews() {
      if (!this.isConnected) return;
      this.previews.forEach((video) => {
        if (video.dataset.inView === 'true' && video.getAttribute('src')) this.play(video);
      });
    }

    // Adaptive HLS where the browser plays it natively (Safari, iOS, Android); MP4 elsewhere.
    load(video) {
      if (video.getAttribute('src')) return;
      const { src, srcHls } = video.dataset;
      const useHls = srcHls && video.canPlayType('application/vnd.apple.mpegurl');
      video.src = useHls || !src ? srcHls : src;
    }

    play(video) {
      const started = video.play();
      if (started && started.catch) started.catch(() => {});
      return started;
    }

    /* Lightbox */

    bindModal() {
      this.closeButton.addEventListener('click', () => this.close());
      this.modalPrev.addEventListener('click', () => this.goTo(this.index - 1));
      this.modalNext.addEventListener('click', () => this.goTo(this.index + 1));
      this.soundButtons.forEach((button) => button.addEventListener('click', () => this.setMuted(!this.muted, true)));

      if (this.slides.length < 2) {
        this.modalPrev.hidden = true;
        this.modalNext.hidden = true;
      }

      this.modal.addEventListener('click', (event) => {
        if (event.target === this.modal || event.target === this.stage || event.target.hasAttribute('data-reels-slide')) {
          this.close();
        }
      });

      this.videos.forEach((video) => {
        const frame = video.parentElement;
        video.addEventListener('click', () => (video.paused ? this.play(video) : video.pause()));
        video.addEventListener('play', () => frame.classList.remove('is-paused'));
        video.addEventListener('pause', () => {
          if (this.isOpen() && this.videos[this.index] === video) frame.classList.add('is-paused');
        });
      });

      this.stage.addEventListener(
        'touchstart',
        (event) => {
          const touch = event.changedTouches[0];
          this.touchStart = { x: touch.clientX, y: touch.clientY };
        },
        { passive: true }
      );
      this.stage.addEventListener(
        'touchend',
        (event) => {
          if (!this.touchStart) return;
          const touch = event.changedTouches[0];
          const dx = touch.clientX - this.touchStart.x;
          const dy = touch.clientY - this.touchStart.y;
          this.touchStart = null;
          if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) this.goTo(this.index + (dx < 0 ? 1 : -1));
        },
        { passive: true }
      );

      this.setMuted(false);
    }

    isOpen() {
      return Boolean(this.modal && this.modal.classList.contains('is-open'));
    }

    open(index, trigger) {
      clearTimeout(this.closeTimer);
      this.lastFocus = trigger || document.activeElement;
      this.previews.forEach((video) => video.pause());

      this.modal.hidden = false;
      void this.modal.offsetWidth;
      this.modal.classList.add('is-open');
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', this.onKeydown);

      // Called inside the click handler so the browser allows playback with sound.
      this.goTo(index);
      this.closeButton.focus({ preventScroll: true });
    }

    close(restoreFocus = true) {
      this.modal.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', this.onKeydown);

      this.videos.forEach((video) => {
        video.pause();
        if (video.getAttribute('src')) video.currentTime = 0;
      });
      this.slides.forEach((slide) => slide.querySelector('.iwao-reels-modal__frame').classList.remove('is-paused'));

      this.closeTimer = setTimeout(() => {
        if (this.modal) this.modal.hidden = true;
      }, 250);

      if (restoreFocus && this.lastFocus && this.lastFocus.isConnected) this.lastFocus.focus({ preventScroll: true });
      this.resumePreviews();
    }

    goTo(index) {
      if (index < 0 || index >= this.slides.length) return;
      this.index = index;

      this.slides.forEach((slide, i) => {
        const active = i === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        if (!active) this.videos[i].pause();
      });

      this.modalPrev.disabled = index === 0;
      this.modalNext.disabled = index === this.slides.length - 1;
      const focused = document.activeElement;
      if (this.isOpen() && (!this.modal.contains(focused) || focused.disabled)) {
        (this.modalPrev.disabled ? this.modalNext : this.modalPrev).focus({ preventScroll: true });
      }

      const video = this.videos[index];
      this.load(video);
      video.muted = this.muted;

      const started = video.play();
      if (started && started.catch) {
        started.catch((error) => {
          // Browser refused playback with sound: fall back to muted autoplay.
          if (error && error.name === 'NotAllowedError' && !video.muted) {
            this.setMuted(true);
            this.play(video);
          }
        });
      }
    }

    setMuted(muted, userAction = false) {
      this.muted = muted;
      this.modal.classList.toggle('is-muted', muted);
      this.videos.forEach((video) => (video.muted = muted));

      const label = muted ? this.dataset.labelUnmute : this.dataset.labelMute;
      this.soundButtons.forEach((button) => {
        button.setAttribute('aria-label', label);
        button.setAttribute('aria-pressed', muted ? 'true' : 'false');
      });

      const video = this.videos[this.index];
      if (userAction && video && video.paused && this.isOpen()) this.play(video);
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      } else if (event.key === 'ArrowLeft') {
        this.goTo(this.index - 1);
      } else if (event.key === 'ArrowRight') {
        this.goTo(this.index + 1);
      } else if (event.key === 'Tab') {
        this.trapFocus(event);
      }
    }

    trapFocus(event) {
      const focusable = [this.soundButtons[this.index], this.closeButton, this.modalPrev, this.modalNext].filter(
        (el) => el && !el.disabled && !el.hidden
      );
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const current = focusable.indexOf(document.activeElement);

      if (current === -1) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('iwao-video-reels', IwaoVideoReels);
}
